import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { API_BASE_URL } from './config/api';
import { Toaster } from 'react-hot-toast';

// Components
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import Customers from './pages/Customers/Customers';
import CustomerDetail from './pages/Customers/CustomerDetail';
import EditCustomer from './pages/Customers/EditCustomer';
import NewCustomer from './pages/Customers/NewCustomer';
import Products from './pages/Products/Products';
import EditProduct from './pages/Products/EditProduct';
import ProductDetail from './pages/Products/ProductDetail';
import Contracts from './pages/Contracts/Contracts';
import ContractDetail from './pages/Contracts/ContractDetail';
import EditContract from './pages/Contracts/EditContract';
import NewContract from './pages/Contracts/NewContract';
import ContractPricing from './pages/Contracts/ContractPricing';
import ContractCustomers from './pages/Contracts/ContractCustomers';
import ContractAliases from './pages/Contracts/ContractAliases';
import Chargebacks from './pages/Chargebacks/Chargebacks';
import ChargebackDetail from './pages/Chargebacks/ChargebackDetail';
import EditChargeback from './pages/Chargebacks/EditChargeback';
import NewChargeback from './pages/Chargebacks/NewChargeback';
import ChargebackProcessingDashboard from './pages/Chargebacks/ChargebackProcessingDashboard';
import ChargebackLineItemEditor from './pages/Chargebacks/ChargebackLineItemEditor';
import ChargebackValidationInterface from './pages/Chargebacks/ChargebackValidationInterface';
import ChargebackList from './components/ChargebackList';
import Batches from './pages/Batches/Batches';
import BatchDetail from './pages/Batches/BatchDetail';
import BatchLineItems from './pages/Batches/BatchLineItems';
import LineItemDetails from './pages/LineItems/LineItemDetails';
import GPODashboard from './pages/GPOs/GPODashboard';
import Rebates from './pages/Rebates/Rebates';
import RebateDetail from './pages/Rebates/RebateDetail';
import GrossToNet from './pages/GTN/GrossToNet';
import Upload from './pages/Upload/Upload';
import Admin from './pages/Admin/Admin';
import TestDataManagement from './pages/Admin/TestDataManagement';
import UserPermissions from './pages/Admin/UserPermissions';
import ProblemReports from './pages/Admin/ProblemReports';
import UserGuide from './pages/Guide/UserGuide';
import Edi844Validation from './pages/Guide/Edi844Validation';
import StatusCodes from './pages/Guide/StatusCodes';
import CompanyCOTManagement from './pages/Admin/CompanyCOTManagement';
import EDI from './pages/EDI/EDI';
import WholesalerEdiMapping from './pages/EDI/WholesalerEdiMapping';
import Reports from './pages/Reports/Reports';
import Profile from './pages/Profile/Profile';
import AMPDashboard from './pages/AMP/AMPDashboard';
import GovernmentPricing from './pages/GovernmentPricing/GovernmentPricing';
import IraRebates from './pages/IraRebates/IraRebates';
import Finance from './pages/Finance/Finance';
import AMPCalculation from './pages/AMP/AMPCalculation';
import SalesDataDashboard from './pages/SalesData/SalesDataDashboard';
import LandingPage from './pages/Landing/LandingPage';
import AboutUs from './pages/About/AboutUs';
import ContactUs from './pages/Contact/ContactUs';
import Features from './pages/Features/Features';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, isLoading } = useAuth();


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !user.roles?.some(role => allowedRoles.includes(role))) {
    return <Navigate to="/app/dashboard" replace />;
  }
  return children;
};

// Main App Routes
const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/features" element={<Features />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Protected Routes */}
      <Route path="/app" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Customer Routes */}
        <Route path="customers" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <Customers />
          </ProtectedRoute>
        } />
        <Route path="customers/:id" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <CustomerDetail />
          </ProtectedRoute>
        } />
        <Route path="customers/:id/edit" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <EditCustomer />
          </ProtectedRoute>
        } />
        <Route path="customers/new" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <NewCustomer />
          </ProtectedRoute>
        } />
        
        {/* Product Routes */}
        <Route path="products" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <Products />
          </ProtectedRoute>
        } />
        <Route path="products/new" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <EditProduct />
          </ProtectedRoute>
        } />
        <Route path="products/:id/edit" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <EditProduct />
          </ProtectedRoute>
        } />
        <Route path="products/:id" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ProductDetail />
          </ProtectedRoute>
        } />
        
        {/* Contract Routes */}
        <Route path="contracts" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <Contracts />
          </ProtectedRoute>
        } />
        <Route path="contracts/:id/edit" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <EditContract />
          </ProtectedRoute>
        } />
        <Route path="contracts/new" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <NewContract />
          </ProtectedRoute>
        } />
        <Route path="contracts/:id" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ContractDetail />
          </ProtectedRoute>
        } />
        <Route path="contracts/:id/pricing" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ContractPricing />
          </ProtectedRoute>
        } />
        <Route path="contracts/:id/customers" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ContractCustomers />
          </ProtectedRoute>
        } />
        <Route path="contracts/:id/aliases" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ContractAliases />
          </ProtectedRoute>
        } />
        
        {/* Chargeback Routes */}
        <Route path="chargebacks" element={<Chargebacks />} />
        <Route path="chargebacks/enhanced" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ChargebackList />
          </ProtectedRoute>
        } />
        <Route path="chargebacks/processing" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ChargebackProcessingDashboard />
          </ProtectedRoute>
        } />
        <Route path="chargebacks/:id" element={<ChargebackDetail />} />
        <Route path="chargebacks/:id/edit" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <EditChargeback />
          </ProtectedRoute>
        } />
        <Route path="chargebacks/:id/validate" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ChargebackValidationInterface />
          </ProtectedRoute>
        } />
        <Route path="chargebacks/:claimId/line-items" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <ChargebackLineItemEditor />
          </ProtectedRoute>
        } />
        <Route path="chargebacks/new" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <NewChargeback />
          </ProtectedRoute>
        } />
        
        {/* Batch Routes */}
        <Route path="batches" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <Batches />
          </ProtectedRoute>
        } />
        <Route path="batches/:id" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <BatchDetail />
          </ProtectedRoute>
        } />
        <Route path="batches/:batchId/line-items" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <BatchLineItems />
          </ProtectedRoute>
        } />
        <Route path="line-items/:lineItemId" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <LineItemDetails />
          </ProtectedRoute>
        } />
        <Route path="gpos" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <GPODashboard />
          </ProtectedRoute>
        } />

        {/* Commercial rebate programs */}
        <Route path="rebates" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <Rebates />
          </ProtectedRoute>
        } />
        <Route path="rebates/:id" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <RebateDetail />
          </ProtectedRoute>
        } />

        {/* Gross-to-Net analytics */}
        <Route path="gtn" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <GrossToNet />
          </ProtectedRoute>
        } />

        {/* Upload Routes */}
        <Route path="upload" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <Upload />
          </ProtectedRoute>
        } />
        
        {/* EDI Routes */}
        <Route path="edi" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <EDI />
          </ProtectedRoute>
        } />
        <Route path="edi/wholesaler-mapping" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <WholesalerEdiMapping />
          </ProtectedRoute>
        } />
        
        {/* Reports Routes */}
        <Route path="reports" element={<Reports />} />
        
        {/* Admin Routes */}
        <Route path="admin" element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <Admin />
          </ProtectedRoute>
        } />
        <Route path="admin/test-data" element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <TestDataManagement />
          </ProtectedRoute>
        } />
        <Route path="admin/user-permissions" element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <UserPermissions />
          </ProtectedRoute>
        } />
        <Route path="admin/problem-reports" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF']}>
            <ProblemReports />
          </ProtectedRoute>
        } />
        <Route path="admin/company-cot" element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <CompanyCOTManagement />
          </ProtectedRoute>
        } />
        
        {/* Profile Route */}
        <Route path="profile" element={<Profile />} />
        <Route path="guide" element={<UserGuide />} />
        <Route path="guide/edi-844-validation" element={<Edi844Validation />} />
        <Route path="guide/status-codes" element={<StatusCodes />} />

        {/* AMP Routes */}
        <Route path="amp" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <AMPDashboard />
          </ProtectedRoute>
        } />
        <Route path="government-pricing" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <GovernmentPricing />
          </ProtectedRoute>
        } />
        <Route path="ira-rebates" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'FINANCE_AR']}>
            <IraRebates />
          </ProtectedRoute>
        } />
        <Route path="finance" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'FINANCE_AR']}>
            <Finance />
          </ProtectedRoute>
        } />
        <Route path="amp/calculate/:productId" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <AMPCalculation />
          </ProtectedRoute>
        } />
        
        {/* Sales Data Routes */}
        <Route path="sales-data" element={
          <ProtectedRoute allowedRoles={['ADMIN', 'STAFF', 'CHARGEBACK_ANALYST', 'CONTRACT_ADMIN', 'FINANCE_AR', 'ACCOUNT_MANAGER']}>
            <SalesDataDashboard />
          </ProtectedRoute>
        } />
        
      </Route>
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
  );
};

// Main App Component
const App = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
